import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import {
  MONTH_LABELS_PT_SHORT,
  computeMonthlyMatrix,
  type MonthlyRow,
  type MonthlySection,
} from '@/core/aggregation/monthly'
import type { Entry } from '@/types/domain'
import { formatBRL, formatOrig } from '../format'
import { Card, CardContent, CardHeader } from '../shadcn/card'

export interface MonthlyMatrixProps {
  entries: Entry[]
  anoBase: number
}

export function MonthlyMatrixView({ entries, anoBase }: MonthlyMatrixProps) {
  const matrix = computeMonthlyMatrix(entries)

  return (
    <div className="space-y-5">
      <MonthlyCard
        title="Rendimentos por mês"
        anoBase={anoBase}
        section={matrix.rendimentos}
        accent="text-success"
        accentIcon={<ArrowUpRight className="h-4 w-4 text-success" />}
      />
      <MonthlyCard
        title="Impostos pagos no exterior por mês"
        anoBase={anoBase}
        section={matrix.impostos}
        accent="text-destructive"
        accentIcon={<ArrowDownRight className="h-4 w-4 text-destructive" />}
      />
    </div>
  )
}

function MonthlyCard({
  title,
  anoBase,
  section,
  accent,
  accentIcon,
}: {
  title: string
  anoBase: number
  section: MonthlySection
  accent: string
  accentIcon: React.ReactNode
}) {
  // Hide currency rows that carry no value across the whole year (keeps the
  // table tight when the user only uses one moeda).
  const visibleRows = section.rows.filter((row) => row.total !== 0)
  const hasAnyData = visibleRows.length > 0

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between border-b border-border bg-muted/20 py-4 space-y-0">
        <div className="flex items-center gap-2">
          {accentIcon}
          <h2 className="font-display text-xl tracking-tight font-normal leading-none">
            {title}
          </h2>
        </div>
        <div className="text-right">
          <div className="text-muted-foreground text-[10px] tracking-widest2 uppercase">
            Total {anoBase}
          </div>
          <div className={`font-mono text-xl font-medium ${accent}`}>
            R$ {formatBRL(section.yearTotalBRL)}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {hasAnyData ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="text-muted-foreground border-b border-border bg-background">
                  <th className="sticky left-0 z-10 bg-background px-4 py-2 text-left text-[10px] font-medium tracking-widest2 uppercase">
                    Moeda
                  </th>
                  {MONTH_LABELS_PT_SHORT.map((m, idx) => (
                    <th
                      key={idx}
                      className="px-2 py-2 text-right text-[10px] font-medium tracking-widest2 uppercase"
                    >
                      {m}
                    </th>
                  ))}
                  <th className="bg-muted/20 border-l border-border px-3 py-2 text-right text-[10px] font-medium tracking-widest2 uppercase">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <MonthRow key={row.key} row={row} accent={accent} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground px-6 py-8 text-center text-sm italic">
            nenhum lançamento desta natureza no período
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function MonthRow({ row, accent }: { row: MonthlyRow; accent: string }) {
  const isBRL = row.key === 'BRL'
  const labelClass = isBRL
    ? `font-mono text-[13px] font-medium ${accent}`
    : 'font-mono text-[12px] text-muted-foreground'
  const totalClass = isBRL ? `font-mono font-medium ${accent}` : 'font-mono text-muted-foreground'

  const fmt = isBRL ? formatBRL : formatOrig

  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/30">
      <td className={`sticky left-0 z-10 bg-background px-4 py-2 ${labelClass}`}>
        {row.key}
      </td>
      {row.months.map((value, idx) => (
        <td
          key={idx}
          className={`px-2 py-2 text-right font-mono text-[12px] select-text ${
            value === 0 ? 'text-muted-foreground/40' : ''
          }`}
        >
          {value === 0 ? '—' : fmt(value)}
        </td>
      ))}
      <td className={`bg-muted/20 border-l border-border px-3 py-2 text-right ${totalClass} select-text`}>
        {row.total === 0 ? '—' : fmt(row.total)}
      </td>
    </tr>
  )
}
